import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  C, PageHeader, GlassCard, Stat, Badge, Sparkline, Skeleton, ProgressBar, Button, statusColor,
} from "@coremonit/design-system";
import {
  LayoutDashboard, PlusCircle, Sparkles, Activity, ArrowUpRight,
  Cpu, MemoryStick, HardDrive, Wifi, Zap, Flame, AlertTriangle,
  RotateCcw, Bomb, HeartPulse, Rocket, FolderKanban, Users2, Boxes,
} from "lucide-react";
import { useOverview, useServices, useIncidents, useProjectsSummary, useProjects } from "../api/hooks";
import { ProjectTile } from "./Projects";

export function OverviewPage() {
  const overview = useOverview();
  const services = useServices();
  const incidents = useIncidents();
  const projectsSummary = useProjectsSummary();
  const projectsList = useProjects();
  const nav = useNavigate();

  const loading = overview.isLoading || services.isLoading;
  const o = overview.data;
  const projSummary = projectsSummary.data;
  const projItems = (projectsList.data?.items ?? []).slice(0, 8);

  const errorSpark = useMemo(() => Array.from({ length: 24 }, (_, i) => Math.max(0.1, (i % 5) + Math.random() * 3)), []);
  const throughputSpark = useMemo(() => Array.from({ length: 24 }, () => 400 + Math.random() * 700), []);
  const latencySpark = useMemo(() => Array.from({ length: 24 }, () => 80 + Math.random() * 120), []);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <PageHeader
        icon={<LayoutDashboard size={16} />}
        title="Microservice Overview"
        subtitle={o ? `${projSummary?.totals.projects ?? 0} projects · ${o.totals.services} services · last update ${new Date(o.updatedAt).toLocaleTimeString()}` : "Loading…"}
        badge={o && o.totals.critical > 0 ? <Badge color="red">{o.totals.critical} CRITICAL</Badge> : <Badge color="green">HEALTHY</Badge>}
        actions={
          <>
            <Button variant="secondary" icon={<Activity size={12} />} onClick={() => nav("/map")}>Service Map</Button>
            <Button variant="glow" icon={<PlusCircle size={12} />} onClick={() => nav("/services/new")}>Add Microservice</Button>
          </>
        }
      />

      {/* Row 1: totals grid — every tile is a click-through to a filtered view */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
        {loading ? (
          Array.from({ length: 6 }, (_, i) => <Skeleton key={i} height={82} radius={14} />)
        ) : (
          <>
            <Stat label="Projects"       value={projSummary?.totals.projects ?? "—"} icon={<FolderKanban size={11} />} color={C.blue} sublabel={`${projSummary?.totals.members ?? 0} members`} onClick={() => nav("/projects")} />
            <Stat label="Total services" value={o!.totals.services} icon={<Sparkles size={11} />} color={C.text} onClick={() => nav("/services")} />
            <Stat label="Healthy"        value={o!.totals.healthy}  icon={<HeartPulse size={11} />} color={C.green} sublabel={`${((o!.totals.healthy / o!.totals.services) * 100).toFixed(0)}% of fleet`} onClick={() => nav("/services?status=healthy")} />
            <Stat label="Warning"        value={o!.totals.warning}  icon={<AlertTriangle size={11} />} color={C.amber} onClick={() => nav("/services?status=warning")} />
            <Stat label="Critical"       value={o!.totals.critical} icon={<Flame size={11} />} color={C.red}   sublabel={o!.totals.critical > 0 ? "Investigate now" : "Nothing burning"} onClick={() => nav("/services?status=critical")} />
            <Stat label="Unknown"        value={o!.totals.unknown}  color={C.textMuted} sublabel="No recent telemetry" onClick={() => nav("/services?status=unknown")} />
          </>
        )}
      </div>

      {/* Project health — click a plate to filter the projects list */}
      {!loading && projSummary && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10 }}>
          <Stat label="Healthy projects"  value={projSummary.byStatus.healthy}  icon={<HeartPulse size={11} />} color={C.green} onClick={() => nav("/projects?status=healthy")} />
          <Stat label="Warning projects"  value={projSummary.byStatus.warning}  icon={<AlertTriangle size={11} />} color={C.amber} onClick={() => nav("/projects?status=warning")} />
          <Stat label="Critical projects" value={projSummary.byStatus.critical} icon={<Flame size={11} />} color={C.red}   sublabel={projSummary.byStatus.critical > 0 ? "Needs attention" : undefined} onClick={() => nav("/projects?status=critical")} />
          <Stat label="Project services"  value={projSummary.totals.services}   icon={<Boxes size={11} />} color={C.cyan}  sublabel="across all projects" onClick={() => nav("/services")} />
          <Stat label="Project members"   value={projSummary.totals.members}    icon={<Users2 size={11} />} color={C.purple} onClick={() => nav("/projects")} />
        </div>
      )}

      {/* Projects grid — every plate is clickable to drill in */}
      <GlassCard style={{ padding: 18 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
          <SectionTitle title="Projects" icon={<FolderKanban size={13} />} />
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {projSummary && <Badge color="blue">{projSummary.totals.projects} total</Badge>}
            <Button variant="ghost" iconRight={<ArrowUpRight size={12} />} onClick={() => nav("/projects")}>See all</Button>
          </div>
        </div>
        {projectsSummary.isLoading ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 10 }}>
            {Array.from({ length: 4 }, (_, i) => <Skeleton key={i} height={180} radius={16} />)}
          </div>
        ) : projItems.length ? (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 10 }}>
            {projItems.map((p) => (
              <ProjectTile key={p.id} p={p} onOpen={() => nav(`/projects/${p.key}`)} />
            ))}
          </div>
        ) : (
          <div style={{ color: C.textMuted, fontSize: 12, padding: 8 }}>No projects yet — <button onClick={() => nav("/projects")} style={{ background: "transparent", border: "none", color: C.blue, cursor: "pointer", fontWeight: 700 }}>create one</button>.</div>
        )}
      </GlassCard>

      {/* Row 2: latency + traffic hero */}
      <div style={{ display: "grid", gridTemplateColumns: "2fr 3fr", gap: 12 }}>
        <GlassCard style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Latency</div>
              <div style={{ color: C.text, fontSize: 22, fontWeight: 700, marginTop: 4 }}>{o ? `${o.latency.p95} ms` : "—"} <span style={{ color: C.textMuted, fontSize: 11, fontWeight: 500 }}>p95</span></div>
            </div>
            <Sparkline data={latencySpark} color={C.blue} width={140} height={40} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <MiniStat label="p50" value={o ? `${o.latency.p50}ms` : "—"} color={C.green} />
            <MiniStat label="p95" value={o ? `${o.latency.p95}ms` : "—"} color={C.blue} />
            <MiniStat label="p99" value={o ? `${o.latency.p99}ms` : "—"} color={C.amber} />
          </div>
        </GlassCard>
        <GlassCard style={{ padding: 18 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <div>
              <div style={{ color: C.textMuted, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5, fontWeight: 700 }}>Throughput</div>
              <div style={{ color: C.text, fontSize: 22, fontWeight: 700, marginTop: 4 }}>{o ? o.throughputRpm.toLocaleString() : "—"} <span style={{ color: C.textMuted, fontSize: 11, fontWeight: 500 }}>rpm total</span></div>
            </div>
            <Sparkline data={throughputSpark} color={C.cyan} width={220} height={40} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 8 }}>
            <MiniStat label="Availability"    value={o ? `${o.availabilityPct.toFixed(2)}%` : "—"} color={C.green} />
            <MiniStat label="Error rate"      value={o ? `${o.errorRatePct.toFixed(2)}%` : "—"}  color={o && o.errorRatePct > 1 ? C.red : C.green} />
            <MiniStat label="Deploy success"  value={o ? `${o.deploymentSuccessPct}%`   : "—"}  color={C.blue} />
            <MiniStat label="Crash loops"     value={o ? String(o.crashLoops)           : "—"}  color={o && o.crashLoops > 0 ? C.red : C.textSub} />
          </div>
        </GlassCard>
      </div>

      {/* Row 3: infra saturation + reliability row */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12 }}>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Infrastructure saturation" icon={<Cpu size={13} />} />
          {loading ? <Skeleton height={100} /> : (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginTop: 12 }}>
              <BarRow icon={<Cpu size={12} />}         label="CPU"    value={o!.saturation.cpuPct}     color={C.blue}   suffix="%" />
              <BarRow icon={<MemoryStick size={12} />} label="Memory" value={o!.saturation.memPct}     color={C.purple} suffix="%" />
              <BarRow icon={<HardDrive size={12} />}   label="Disk"   value={o!.saturation.diskPct}    color={C.cyan}   suffix="%" />
              <BarRow icon={<Wifi size={12} />}        label="Net"    value={o!.saturation.networkMbps} color={C.green}  suffix=" Mbps" max={1200} />
            </div>
          )}
        </GlassCard>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Reliability" icon={<Zap size={13} />} />
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
            <MiniStat label="Restarts 24h"       value={o?.restarts24h ?? "—"} color={C.amber} icon={<RotateCcw size={10} />} />
            <MiniStat label="OOM kills 24h"      value={o?.oomKills24h ?? "—"} color={C.red}   icon={<Bomb size={10} />} />
            <MiniStat label="Deploy success"     value={o ? `${o.deploymentSuccessPct}%` : "—"} color={C.blue}  icon={<Rocket size={10} />} />
            <MiniStat label="Availability (avg)" value={o ? `${o.availabilityPct.toFixed(2)}%` : "—"} color={C.green} icon={<HeartPulse size={10} />} />
          </div>
        </GlassCard>
      </div>

      {/* Row 4: heatmap + top bottlenecks */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12 }}>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Service load — last 24 hours" icon={<Activity size={13} />} />
          {loading ? <Skeleton height={220} /> : (
            <Heatmap data={o!.heatmap} services={services.data?.items.slice(0, o!.heatmap.length).map((s) => s.name) ?? []} />
          )}
        </GlassCard>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Top bottlenecks" icon={<Flame size={13} />} />
          {loading ? <Skeleton height={220} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {(o!.topBottlenecks.length ? o!.topBottlenecks : []).map((b) => (
                <button
                  key={b.serviceId + b.kind}
                  onClick={() => nav(`/services/${b.serviceId}`)}
                  style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    padding: "10px 12px",
                    background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12,
                    cursor: "pointer", textAlign: "left",
                  }}
                >
                  <div>
                    <div style={{ color: C.text, fontSize: 12, fontWeight: 600 }}>{b.serviceName}</div>
                    <div style={{ color: C.textMuted, fontSize: 10, marginTop: 2 }}>{b.kind} · {b.metric}</div>
                  </div>
                  <Badge color={b.severity === "critical" ? "red" : b.severity === "high" ? "amber" : "blue"}>{b.severity}</Badge>
                </button>
              ))}
              {(!o!.topBottlenecks.length) && <div style={{ color: C.textMuted, fontSize: 11, padding: 12 }}>All services within SLO.</div>}
            </div>
          )}
        </GlassCard>
      </div>

      {/* Row 5: AI summary + recent incidents */}
      <div style={{ display: "grid", gridTemplateColumns: "3fr 2fr", gap: 12 }}>
        <GlassCard glow accent={C.borderBright} style={{ padding: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <div style={{ width: 26, height: 26, borderRadius: 8, background: `linear-gradient(135deg, ${C.blue}, ${C.cyan})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Sparkles size={13} color="#fff" />
            </div>
            <div>
              <div style={{ color: C.text, fontSize: 12, fontWeight: 700 }}>AI Summary</div>
              <div style={{ color: C.textMuted, fontSize: 10 }}>Fleet-wide analysis · {o ? new Date(o.updatedAt).toLocaleTimeString() : "…"}</div>
            </div>
          </div>
          {loading ? <Skeleton height={60} /> : (
            <div style={{ color: C.textSub, fontSize: 12.5, lineHeight: 1.6 }}>{o!.aiSummary}</div>
          )}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <Button variant="secondary" icon={<ArrowUpRight size={12} />} onClick={() => nav("/ai")}>Investigate</Button>
            <Button variant="ghost" onClick={() => nav("/rca")}>Open RCA</Button>
          </div>
        </GlassCard>
        <GlassCard style={{ padding: 18 }}>
          <SectionTitle title="Recent incidents" icon={<AlertTriangle size={13} />} />
          {incidents.isLoading ? <Skeleton height={180} /> : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginTop: 8 }}>
              {incidents.data?.items.slice(0, 5).map((inc) => (
                <button
                  key={inc.id}
                  onClick={() => nav(`/services/${inc.serviceId}`)}
                  style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 12, padding: 10, textAlign: "left", cursor: "pointer" }}
                >
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                    <div style={{ color: C.text, fontSize: 11, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{inc.title}</div>
                    <Badge color={inc.severity === "critical" ? "red" : "amber"}>{inc.status}</Badge>
                  </div>
                  <div style={{ color: C.textMuted, fontSize: 10, marginTop: 2 }}>{inc.ruleName} · {new Date(inc.startedAt).toLocaleTimeString()}</div>
                </button>
              ))}
              {!incidents.data?.items.length && <div style={{ color: C.textMuted, fontSize: 11, padding: 12 }}>No incidents.</div>}
            </div>
          )}
        </GlassCard>
      </div>
    </div>
  );
}

// ─── local helpers ────────────────────────────────────────────────────────

function SectionTitle({ title, icon }: { title: string; icon?: React.ReactNode }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <span style={{ color: C.blue, display: "inline-flex" }}>{icon}</span>
      <div style={{ color: C.textSub, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4 }}>{title}</div>
    </div>
  );
}

function MiniStat({ label, value, color, icon }: { label: string; value: React.ReactNode; color: string; icon?: React.ReactNode }) {
  return (
    <div style={{ background: C.surfaceHigh, border: `1px solid ${C.border}`, borderRadius: 10, padding: "8px 10px" }}>
      <div style={{ color: C.textMuted, fontSize: 9, textTransform: "uppercase", letterSpacing: 0.4, fontWeight: 700, display: "flex", alignItems: "center", gap: 4 }}>
        {icon}{label}
      </div>
      <div style={{ color, fontSize: 14, fontWeight: 700, marginTop: 3, fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function BarRow({ label, value, color, suffix, max = 100, icon }: { label: string; value: number; color: string; suffix?: string; max?: number; icon?: React.ReactNode }) {
  const pct = Math.min(100, (value / max) * 100);
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 4 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, color: C.textSub, fontSize: 11 }}>{icon}{label}</div>
        <div style={{ color, fontSize: 12, fontWeight: 700 }}>{value}{suffix}</div>
      </div>
      <ProgressBar value={pct} color={color} />
    </div>
  );
}

function Heatmap({ data, services }: { data: number[][]; services: string[] }) {
  const bucket = (v: number) => {
    if (v > 85) return C.red;
    if (v > 65) return C.amber;
    if (v > 35) return C.blue;
    return C.blueDim;
  };
  return (
    <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "auto 1fr", gap: 8, alignItems: "center" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {services.map((s) => <div key={s} style={{ color: C.textSub, fontSize: 10, height: 14, display: "flex", alignItems: "center" }}>{s}</div>)}
      </div>
      <div>
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          {data.map((row, r) => (
            <div key={r} style={{ display: "grid", gridTemplateColumns: "repeat(24, 1fr)", gap: 3 }}>
              {row.map((v, c) => (
                <div
                  key={c}
                  title={`${services[r]} · ${c}:00 → ${v}`}
                  style={{ height: 14, borderRadius: 3, background: bucket(v), opacity: Math.max(0.35, v / 100) }}
                />
              ))}
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6, color: C.textMuted, fontSize: 9 }}>
          <span>00:00</span><span>06:00</span><span>12:00</span><span>18:00</span><span>now</span>
        </div>
      </div>
    </div>
  );
}
